import classNames from 'classnames';
import React from 'react';
import uniqid from 'uniqid';
import { useParams, useNavigate } from 'react-router-dom';

import GridMatrixCell from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-cell/goals-matrix-cell';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import Loader from 'components/partials/loader/loader';

import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface GoalsMatrixProps {
  headers: string[];
  rows: string[];
  data: Goal[];
  showOnlyDefined: boolean;
  year: string | number;
  loading: boolean;
}

interface CellInput {
  id?: string | number;
  type: string;
  category: string;
}

const GoalsMatrix = ({
  headers,
  rows,
  data,
  showOnlyDefined,
  year,
  loading,
}: GoalsMatrixProps): React.JSX.Element => {
  const { id: clientId } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const getCellData = (row: string, header: string): Goal[] =>
    data.filter((item) => item.category === row && item.type === header);

  const handleCellClick = (cell: CellInput): void => {
    const goalId = cell.id ? '' : 'new';
    const type = cell.type.replace(' ', '-').toLowerCase();
    const category = cell.category.replace(' ', '-').toLowerCase();

    navigate(
      `/admin/clients/${clientId}/goals/${year}/${type}/${category}/${goalId}`
    );
  };

  const filteredHeaders = showOnlyDefined
    ? headers.filter((header) => data.some((item) => item.type === header))
    : headers;

  const filteredRows = showOnlyDefined
    ? rows.filter((row) => data.some((item) => item.category === row))
    : rows;

  const isTableEmpty =
    filteredRows.length === 0 || filteredHeaders.length === 0;

  const headerBasicStyles =
    'p-3 bg-navy-50 text-navy-500 font-semibold text-xs text-left tracking-widest';

  const gridTemplateColumns = `195px repeat(${filteredHeaders.length}, minmax(0, 1fr))`;

  return (
    <div className="overflow-x-auto flex flex-1 flex-col">
      <Loader loading={loading} fullScreen={false} customClass="opacity-60" />
      {isTableEmpty ? (
        <EmptySpaceContent
          customClass="flex-1"
          text="There is no goal to display yet."
        />
      ) : (
        <table
          className="w-full border-separate border-spacing-0 mobile:min-w-max sm:min-w-full"
          data-testid="goals-matrix-table"
        >
          <thead data-testid="goals-matrix-table-header">
            <tr
              className="grid mb-2"
              style={{
                gridTemplateColumns,
              }}
            >
              <th className={`rounded-l-md ${headerBasicStyles}`} scope="col">
                {'Goal Category'.toUpperCase()}
              </th>
              {filteredHeaders.map((header, i) => (
                <th
                  key={header}
                  className={classNames(
                    'border-r border-white',
                    headerBasicStyles,
                    {
                      'rounded-r-md !border-r-0':
                        i === filteredHeaders.length - 1,
                      'border-l': i === 0,
                    }
                  )}
                  scope="col"
                >
                  {header.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="border border-navy-200">
            {filteredRows.map((row, rowIndex) => (
              <tr
                key={uniqid()}
                className={classNames('grid border-spacing-0', {
                  'rounded-t-md': rowIndex === 0,
                  'rounded-b-md': rowIndex === filteredRows.length - 1,
                })}
                style={{
                  gridTemplateColumns,
                }}
              >
                <th
                  className={classNames(
                    'border border-r-0 border-navy-200 p-2 font-semibold',
                    {
                      'rounded-tl-md': rowIndex === 0,
                      'rounded-bl-md': rowIndex === filteredRows.length - 1,
                      'border-t-0': rowIndex !== 0,
                    }
                  )}
                  scope="row"
                >
                  {row}
                </th>
                {filteredHeaders.map((header, i) => {
                  const cellData = getCellData(row, header);

                  return (
                    <td
                      key={header}
                      className={classNames(
                        'border border-navy-200 cursor-pointer',
                        {
                          'rounded-tr-md':
                            rowIndex === 0 && i === filteredHeaders.length - 1,
                          'rounded-br-md':
                            rowIndex === filteredRows.length - 1 &&
                            i === filteredHeaders.length - 1,
                          'border-t-0': rowIndex !== 0,
                          'border-l-0': i !== 0,
                        }
                      )}
                    >
                      <GridMatrixCell
                        cellData={cellData}
                        onClick={handleCellClick}
                        row={row}
                        header={header}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default GoalsMatrix;
